/* eslint-disable no-bitwise */
/* ~~(foo) is equivalent to Math.floor(foo) but sometimes faster */
import type { PeakRange } from '@mimir/web/model/audioTrackConfig';

async function downloadAudio(url: string, index: number, progressCallback: (index: number, progress: number) => void): Promise<ArrayBuffer | null> {
  return new Promise<ArrayBuffer | null>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';

    xhr.addEventListener('progress', (e) => {
      const progress = e.loaded / e.total;
      progressCallback(index, progress);
    });

    xhr.addEventListener('load', _e => {
      if (xhr.status === 200) {
        resolve(xhr.response as ArrayBuffer);
      } else if (xhr.status === 404 || xhr.status === 400) {
        // If we get a 404 (or 400 since the S3 server may return 400),
        // let's return null to indicate that the file does not exist
        resolve(null);
      } else {
        reject(new Error(`Audio channel: ${url} failed to download. Msg: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error(`Audio channel: ${url} failed to download`));
    });

    xhr.send();
  });
}

export const downloadAll = (urls: string[], totalProgressCallback: (progress: number) => void): Promise<(ArrayBuffer | null)[]> => {
  const tp = Array.from<number>({ length: urls.length });
  const progressCallback = (index: number, progress: number) => {
    tp[index] = progress;
    const total = tp.reduce((p: number, cur: number) => p += cur, 0);
    totalProgressCallback(total / urls.length);
  };
  return new Promise<(ArrayBuffer | null)[]>((resolve, reject) => {
    try {
      const downloadJobs: Promise<ArrayBuffer | null>[] = [];
      urls.forEach((url: string, index: number) => {
        tp[index] = 0;
        downloadJobs.push(downloadAudio(url, index, progressCallback));
      });
      Promise.all(downloadJobs).then((res: (ArrayBuffer | null)[]) => {
        resolve(res);
      }).catch((error) => {
        reject(error);
      });
    } catch (error) {
      reject(error);
    }
  });
};

export async function decodeAudio(audioContext: AudioContext, arrayBuffer: ArrayBuffer): Promise<AudioBuffer | null> {
  if (arrayBuffer.byteLength === 0) {
    return null;
  }
  return new Promise<AudioBuffer>((resolve, reject) => {
    /*
      The "void" modifier is specified here to indicate that we're using the old
      variant of decodeAudioData() (to support Safari) rather than the newer one
      that returns a promise. I.e. we expect errors to be propagated in the
      error callback, rather than in a returned promise.
    */
    void audioContext.decodeAudioData(arrayBuffer, buffer => resolve(buffer), err => reject(err));
  });
}

export function getPeaks(
  samples: Float32Array,
  sampleRate: number,
  range: PeakRange,
  width: number,
): number[] {
  const start = range.startInSeconds;
  const end = range.endInSeconds;
  const rangeWidth = end - start;
  const pixelToSampleOffset = (pixel: number) => {
    const normalizedRangeOffset = pixel / width;
    const time = (normalizedRangeOffset * rangeWidth) + start;
    return Math.floor(time * sampleRate);
  };

  const peaks : number[] = [];
  for (let i = 0; i < width; i++) {
    const startSampleOffset = Math.max(pixelToSampleOffset(i), 0);
    const endSampleOffset = Math.min(pixelToSampleOffset(i + 1), samples.length);
    let value = 0.0;
    for (let j = startSampleOffset; j < endSampleOffset; j++) {
      value = Math.max(value, Math.abs(samples[j]));
    }
    peaks.push(value);
  }
  return peaks;
}


/**
 * Unlike getPeaks() that operates on a Float32Array that contains all the audio samples,
 * this function operates on a Uint8Array that contains the currently available peak values.
 * Since the actual media duration may be larger than the media duration we have peaks
 * for, we use peakBufferVirtualSize instead of the buffer size, since peakBufferVirtualSize
 * represents the peak count we would have if we had peaks for the whole media duration.
 */
export function getPeaksFromUint8Array(
  samples: Uint8Array,
  sampleRate: number | null,
  range: PeakRange,
  width: number,
): number[] {
  if (sampleRate === null) {
    // Sometimes bad UI code may provide a non-integer value for width. This
    // will cause this function to throw an error. In the code below the
    // non-integer width is not a problem since it is just used to compare
    // with "i", but it will act as a floor so using this here as well for
    // consistency.
    return Array(Math.floor(width)).fill(0);
  }

  const start = range.startInSeconds;
  const end = range.endInSeconds;
  const rangeWidth = end - start;
  const pixelToSampleOffset = (pixel: number) => {
    const normalizedRangeOffset = pixel / width;
    const time = (normalizedRangeOffset * rangeWidth) + start;
    return Math.floor(time * sampleRate);
  };

  const peaks : number[] = [];
  for (let i = 0; i < width; i++) {
    const startSampleOffset = Math.max(pixelToSampleOffset(i), 0);
    const endSampleOffset = Math.min(pixelToSampleOffset(i + 1), samples.length);
    let value = 0.0;
    for (let j = startSampleOffset; j < endSampleOffset; j++) {
      value = Math.max(value, Math.abs(samples[j]));
    }
    peaks.push(value / 255.0);
  }
  return peaks;
}

export const drawPeaks = (peaks: number[], ctx: CanvasRenderingContext2D, y: number, width: number, height: number, scale: number = 1) => {
  if (peaks.length === 0) {
    return;
  }
  const halvPixel = 0.5 / window.devicePixelRatio;
  const length = peaks.length;
  const max = 1;
  ctx.fillStyle = '#85C1CF';
  ctx.strokeStyle = '#85C1CF';
  ctx.beginPath();
  ctx.moveTo(halvPixel, height + y);
  for (let i = 0; i < length; i++) {
    const h = Math.round(peaks[i] / max * height);
    ctx.lineTo(i * scale + halvPixel, (height - h) + y);
  }
  ctx.lineTo(width + halvPixel, height + y);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(0, (height - halvPixel) + y, width, halvPixel);
  ctx.stroke();
};

export const getHDPContext = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  devicePixelRatio: number,
): CanvasRenderingContext2D => {
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  canvas.width = width * devicePixelRatio;
  canvas.height = height * devicePixelRatio;
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  if (devicePixelRatio > 1) {
    ctx.scale(devicePixelRatio, devicePixelRatio);
  }
  return ctx;
};
